/* eslint-disable no-undef */
// App.js - 国际站版本
// 直接替换你现有App.js文件的开头部分（从开头到第一个useCallback之前）
import About from './About';
import Contact from './Contact';
import PrivacyPolicy from './PrivacyPolicy';
import './App.css';
import PersonalCenter from './PersonalCenter';
import React, { useState, useEffect, useRef, useCallback, useMemo, Suspense } from 'react';
import { auth, db } from './firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';

function App() {
  // === 优化后的状态管理 ===
  const [isHovering, setIsHovering] = useState(false); // LOGO旋转
  // 基础数据状态
  const [words, setWords] = useState([]);
  const [filteredWords, setFilteredWords] = useState([]);
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [inputValue, setInputValue] = useState('');
  const [dailyProgress, setDailyProgress] = useState({});
  const [category, setCategory] = useState(() => {
    // 从 localStorage 读取上次的级别，如果没有则默认为 'elementary'
    const savedCategory = localStorage.getItem('lastCategory');
    return savedCategory || 'elementary';
  });
  const [currentPage, setCurrentPage] = useState('learning');
  const [pronunciationType, setPronunciationType] = useState('us'); // 默认美式发音
  
  // 用户相关状态
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userProfile, setUserProfile] = useState({
    displayName: '',
    photoURL: '',
  });
  const [completedCounts, setCompletedCounts] = useState({});
  
  // 功能设置状态
  const [keyboardSound, setKeyboardSound] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const [loopPlay, setLoopPlay] = useState(false);
  const [interaction, setInteraction] = useState(false);
  
  // 搜索相关状态
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  
  // === 合并的UI状态（优化：减少useState数量）===
  const [uiState, setUiState] = useState({
    isWordInputFocused: false,
    showLoading: false,
    isInputIncorrect: false,
    isMasterLoading: false,
    isIncorrectWord: false,
    searchMode: false,
    // 新增削词相关状态
    isInMasterMode: false,        // 是否处于削词模式（单词已隐藏）
    masterModeWord: '',           // 削词模式下的目标单词
    masterModeFailedWords: {},    // 削词失败的单词记录 {word: true}
  });

  // === 合并的掌握单词状态（优化：统一管理）===
  const [masteredState, setMasteredState] = useState({
    mastered: {},
    manual: {},
    learned: {},
  });

  // 👇 新增：响应式状态
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // 主题状态（带localStorage持久化）
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'light';
  });

  // Refs
  const inputTimerRef = useRef(null);
  const inputRef = useRef(null);
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const audioRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const lastBlurTimeRef = useRef(0);

  // === 优化的计算和辅助函数 ===
  
  // 优化：使用useMemo避免重复创建数组
  const categories = useMemo(() => {
  const categoryCounts = {};
  
  // 统计每个级别的单词数
  words.forEach(word => {
    if (Array.isArray(word.category)) {
      word.category.forEach(cat => {
        const catLower = cat.toLowerCase().trim();
        categoryCounts[catLower] = (categoryCounts[catLower] || 0) + 1;
      });
    } else if (word.category) {
      const catLower = String(word.category).toLowerCase().trim();
      categoryCounts[catLower] = (categoryCounts[catLower] || 0) + 1;
    }
  });

  return [
    { value: 'elementary', label: `小学 (${categoryCounts.elementary || 0} 词)` },
    { value: 'junior', label: `初中 (${categoryCounts.junior || 0} 词)` },
    { value: 'highschool', label: `高中 (${categoryCounts.highschool || 0} 词)` },
    { value: 'cet4', label: `四级 (${categoryCounts.cet4 || 0} 词)` },
    { value: 'cet6', label: `六级 (${categoryCounts.cet6 || 0} 词)` },
    { value: 'toefl', label: `考研/托福 (${categoryCounts.toefl || 0} 词)` },
    { value: 'ielts_gre', label: `雅思/GRE (${categoryCounts.ielts_gre || 0} 词)` },
  ];
}, [words]);

  // 优化：使用useMemo计算当前单词，避免重复计算
  const currentWord = useMemo(() => {
    if (uiState.searchMode) {
      return searchResults[currentWordIndex] || {};
    }
    return filteredWords[currentWordIndex] || {};
  }, [uiState.searchMode, searchResults, filteredWords, currentWordIndex]);

  // 优化：状态更新辅助函数，减少重复代码
  const updateUiState = useCallback((updates) => {
    setUiState(prev => ({ ...prev, ...updates }));
  }, []);

  const updateMasteredState = useCallback((updates) => {
    setMasteredState(prev => ({ ...prev, ...updates }));
  }, []);

  // App.js - 第2部分：进度统计和搜索功能
  // 替换第1部分后面的内容

  // === 乱序函数（Fisher-Yates洗牌算法）===
  const shuffleArray = useCallback((array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  // 主题切换函数
  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const newTheme = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', newTheme);
      return newTheme;
    });
  }, []);

  // === 优化的进度统计计算 ===
  // ✅ 修改后（第158-173行）
  const progressStats = useMemo(() => {
    const totalWords = words.filter((w) => {
      if (Array.isArray(w.category)) {
        return w.category.some(cat => 
          String(cat).toLowerCase().trim() === category.toLowerCase().trim()
        );
      }
      return String(w.category || '').toLowerCase().trim() === category.toLowerCase().trim();
    }).length;
    
    const remainingWords = filteredWords.length;
    
    // ✅ 只统计手动削词
    const totalCumulativeMastered = Object.keys(masteredState.manual).length;
    
    const currentCycleTotalWords = Math.max(totalWords - totalCumulativeMastered, 0);
    const studiedThisCycle = Math.max(currentCycleTotalWords - remainingWords, 0);
    const currentCycleProgress = currentCycleTotalWords > 0 
      ? Math.round((studiedThisCycle / currentCycleTotalWords) * 100) 
      : 0;
    
    // ✅ 只统计手动削词
    const manualMasteredCount = Object.keys(masteredState.manual).length;
    const totalMasteredForProgress = Math.min(manualMasteredCount, totalWords);
    const masteredProgress = Math.round((totalMasteredForProgress / (totalWords || 1)) * 100);

    console.log('进度统计:', {
      totalWords,
      currentCycleTotalWords,
      remainingWords,
      studiedThisCycle,
      currentCycleProgress,
      totalCumulativeMastered
    });

    return {
      totalWords,
      remainingWords,
      currentCycleTotalWords,
      studiedThisCycle,
      currentCycleProgress,
      manualMasteredCount,
      totalMasteredForProgress,
      masteredProgress,
    };
  }, [words, category, filteredWords, masteredState]);

  // === 优化的搜索功能 ===
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      updateUiState({ searchMode: false });
      setSearchResults([]);
      setCurrentWordIndex(0); // 重置索引
      return;
    }

    const results = words
      .filter(word => 
        word.word.toLowerCase().includes(query.toLowerCase()) ||
        (word.meaning_cn && word.meaning_cn.includes(query))
      )
      .slice(0, 100);

    setSearchResults(results);
    setCurrentWordIndex(0); // 重置到第一个结果
    updateUiState({ searchMode: true });
  }, [words, updateUiState]);

  const exitSearchMode = useCallback(() => {
    updateUiState({ searchMode: false });
    setSearchQuery('');
    setSearchResults([]);
  }, [updateUiState]);

  // 修改音频播放函数，添加更好的冲突处理

  // === 优化的音频播放函数 ===
  const playAudio = useCallback(
    (audioPath, word, isLoop = false) => {
      if (!audioPath || audioPath === '') {
        console.log(`无音频: ${word}`);
        return;
      }
      console.log(`播放音频: ${audioPath}, 循环: ${isLoop}`);

      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }

      audioRef.current = new Audio(audioPath);
      audioRef.current.loop = isLoop;

      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log(`成功播放音频: ${word}, 循环: ${isLoop}`);
            setError(null);
          })
          .catch((err) => {
            console.error(`无法播放音频 ${word}: ${err.message}`);
          });
      }

      if (!isLoop) {
        audioRef.current.addEventListener(
          'ended',
          () => {
            if (audioRef.current) {
              audioRef.current.src = '';
              audioRef.current = null;
              console.log(`音频播放结束，清理 audioRef: ${word}`);
            }
          },
          { once: true }
        );
      }
    },
    []
  );

  const toggleLoopPlay = () => {
  setLoopPlay((prev) => {
    const newLoopPlay = !prev;
    if (prev && audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
      console.log('停止循环播放，清理 audioRef');
    } else if (!prev && currentWord.audio?.[pronunciationType] && currentWord.audio[pronunciationType] !== '') {
      setTimeout(() => {
        playAudio(currentWord.audio[pronunciationType], currentWord.word, true);
      }, 100);
    }
    console.log(`循环播放: ${newLoopPlay}`);
    return newLoopPlay;
  });
};

  // App.js - 第3部分：保存进度功能
// 接着第2部分后面

  // === 优化的进度保存功能 ===
  const saveAllProgress = useCallback(
    async (targetCategory) => {
      if (!user || Object.keys(dailyProgress).length === 0) {
        console.log('未登录或无进度，跳过保存');
        return true;
      }

      const maxRetries = 3;
      let attempt = 0;
      while (attempt < maxRetries) {
        try {
          console.log(`保存用户 ${user.uid} 的所有进度, 级别: ${targetCategory}, 尝试: ${attempt + 1}`);
          const progressRef = doc(db, 'users', user.uid, 'progress', targetCategory);
          const inputWords = filteredWords.length;
          const totalWords = filteredWords.length + Object.keys(masteredState.mastered).length;
          const currentWordToStudy =
            filteredWords.length > 0 && currentWordIndex < filteredWords.length
              ? filteredWords[currentWordIndex].word
              : null;

          const progressData = {
            words: { ...dailyProgress },
            lastReviewedWord: currentWordToStudy,
            currentWordIndex: filteredWords.length > 0 ? currentWordIndex : 0,
            inputWords,
            totalWords,
            lastUpdated: Date.now(),
          };

          console.log('保存的 progressData:', JSON.stringify(progressData, (key, value) => {
            if (value === undefined) return 'UNDEFINED';
            return value;
          }, 2));

          await setDoc(progressRef, progressData, { merge: true });
          console.log(
            `成功保存所有进度到 ${targetCategory}, inputWords=${inputWords}, currentWordIndex=${currentWordIndex}, currentWordToStudy=${currentWordToStudy}`
          );
          return true;
        } catch (err) {
          attempt++;
          console.error(`保存所有进度失败 (尝试 ${attempt}/${maxRetries}):`, err);
          if (attempt === maxRetries) {
            setError(`保存所有进度失败: ${err.message}`);
            return false;
          }
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }
    },
    [user, dailyProgress, filteredWords, currentWordIndex, masteredState.mastered]
  );

  const saveProgress = useCallback(
    async (word, reviewCount, lastReviewed, nextReview, mastered = false, inputWords) => {
      if (!user) {
        console.log('未登录，跳过保存单词进度');
        return;
      }
      try {
        console.log(
          `保存单词进度: 用户=${user.uid}, 级别=${category}, 单词=${word}, reviewCount=${reviewCount}, mastered=${mastered}, inputWords=${inputWords}`
        );
        const progressRef = doc(db, 'users', user.uid, 'progress', category);
        const docSnap = await getDoc(progressRef);
        const currentData = docSnap.exists() ? docSnap.data().words || {} : {};

        const updatedWords = {
          ...currentData,
          [word]: {
            reviewCount,
            lastReviewed,
            nextReview,
            mastered,
            manualMaster: mastered && (dailyProgress[word]?.manualMaster || false),
          },
        };

        const remainingWords = filteredWords.filter((w) => w.word !== word);
        let nextWordToStudy = null;
        let newIndex = currentWordIndex;
        if (remainingWords.length > 0) {
          newIndex = Math.min(currentWordIndex, remainingWords.length - 1);
          nextWordToStudy = remainingWords[newIndex].word;
        }

        const progressData = {
          words: updatedWords,
          lastReviewedWord: nextWordToStudy,
          currentWordIndex: newIndex,
          inputWords,
          totalWords: filteredWords.length,
          lastUpdated: Date.now(),
        };

        console.log('保存的 progressData:', JSON.stringify(progressData, (key, value) => {
          if (value === undefined) return 'UNDEFINED';
          return value;
        }, 2));

        await setDoc(progressRef, progressData, { merge: true });

        console.log(
          `成功保存单词进度: ${word}, 级别=${category}, inputWords=${inputWords}, nextWordToStudy=${nextWordToStudy}, currentWordIndex=${newIndex}`
        );
      } catch (err) {
        console.error(`保存单词进度失败: 单词=${word}, 级别=${category}`, {
          code: err.code,
          message: err.message,
          stack: err.stack,
          progressData: JSON.stringify({ word, reviewCount, lastReviewed, nextReview, mastered, inputWords }, null, 2),
        });
        setError(`保存单词 ${word} 进度失败: ${err.message}`);
      }
    },
    [user, category, filteredWords, currentWordIndex, dailyProgress]
  );

  // App.js - 第4部分：手动削词和辅助功能
// 接着第3部分后面

  // === 优化的手动削词功能 ===
  const handleManualMaster = useCallback(
    (word) => {
      if (!user || !word) {
        setError('请先登录以使用削词功能');
        return;
      }

      // 检查是否是削词失败的单词
      if (uiState.masterModeFailedWords[word]) {
        setError('该单词削词失败，请继续学习');
        return;
      }

      // 进入削词模式：隐藏单词，等待用户输入
      updateUiState({ 
        isInMasterMode: true,
        masterModeWord: word 
      });
      setInputValue(''); // 清空输入框
      
      if (inputRef.current) {
        inputRef.current.focus();
      }

      console.log(`进入削词模式: ${word}`);
    },
    [user, uiState.masterModeFailedWords, updateUiState]
  );

  // 新增：实际执行削词的函数
  const executeMasterWord = useCallback(
    async (word) => {
      if (!user || !word) return;

      updateUiState({ isMasterLoading: true });

      try {
        const now = new Date().getTime();
        const newFilteredWords = filteredWords.filter((w) => w.word !== word);
        const newInputWords = newFilteredWords.length;
        const newIndex = newFilteredWords.length > 0 ? Math.min(currentWordIndex, newFilteredWords.length - 1) : 0;

        const updatedDailyProgress = {
          ...dailyProgress,
          [word]: {
            reviewCount: 7,
            lastReviewed: now,
            nextReview: now,
            mastered: true,
            manualMaster: true,
          },
        };

        setDailyProgress(updatedDailyProgress);
        updateMasteredState({
          mastered: { ...masteredState.mastered, [word]: true },
          manual: { ...masteredState.manual, [word]: true }
        });
        setFilteredWords(newFilteredWords);
        setCurrentWordIndex(newIndex);

        const cumulativeMasteredRef = doc(db, 'users', user.uid, 'cumulativeMastered', category);
        const docSnap = await getDoc(cumulativeMasteredRef);
        let cumulativeManualMastered = docSnap.exists() ? docSnap.data().manualMastered || {} : {};
        let cumulativeLearnedMastered = docSnap.exists() ? docSnap.data().learnedMastered || {} : {};

        cumulativeManualMastered[word] = true;
        delete cumulativeLearnedMastered[word];

        await setDoc(
          cumulativeMasteredRef,
          {
            manualMastered: cumulativeManualMastered,
            learnedMastered: cumulativeLearnedMastered,
            lastUpdated: Date.now(),
          },
          { merge: true }
        );

        await saveProgress(word, 7, now, now, true, newInputWords);

        setInputValue('');
        
        // 修改：不退出削词模式，继续保持
        updateUiState({ 
          // isInMasterMode 保持为 true，不改变
          masterModeWord: newFilteredWords.length > 0 ? newFilteredWords[newIndex]?.word : '', // 更新为下一个单词
          isMasterLoading: false 
        });

        if (newFilteredWords.length > 0) {
          console.log(`削词成功，继续削词模式，下一个单词: ${newFilteredWords[newIndex]?.word}, 索引: ${newIndex}`);
        } else {
          // 如果没有单词了，才退出削词模式
          updateUiState({ 
            isInMasterMode: false,
            masterModeWord: ''
          });
          setError(`无 ${category} 类别的未掌握单词`);
        }

        if (inputRef.current) {
          inputRef.current.focus();
        }

        console.log(`削词成功: ${word}，继续削词模式`);
      } catch (err) {
        console.error('削词失败:', {
          word,
          error: err.message,
          stack: err.stack,
        });
        setError(`削词失败: ${err.message}`);
        updateUiState({ isMasterLoading: false });
      }
    },
    [user, filteredWords, saveProgress, category, dailyProgress, currentWordIndex, masteredState, updateUiState, updateMasteredState]
  );

  // === 辅助功能 ===
  // const getReviewHint = useCallback(
  //   (word) => {
  //     if (!dailyProgress[word]) {
  //       return '首次学习';
  //     }

  //     const progress = dailyProgress[word];
  //     const reviewCount = progress.reviewCount || 0;
  //     const intervalDays = [0, 1, 2, 4, 7, 15, 31];

  //     if (reviewCount >= 6) {
  //       return '已完全掌握';
  //     }

  //     const nextDay = intervalDays[reviewCount + 1] || 31;
  //     return `该单词需${nextDay}天后复习`;
  //   },
  //   [dailyProgress]
  // );

  const needsReview = useCallback(
    (word) => {
      if (!dailyProgress[word]) return true;

      const progress = dailyProgress[word];
      const now = new Date().getTime();

      if (progress.mastered) return false;

      return now >= (progress.nextReview || 0);
    },
    [dailyProgress]
  );

  // === 键盘音效 ===
  const playKeySound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (err) {
      console.log('键盘发音失败:', err.message);
    }
  }, []);

  // === 切换功能 ===
  const toggleAutoPlay = () => {
    setAutoPlay((prev) => {
      const newAutoPlay = !prev;
      console.log(`自动播放: ${newAutoPlay}`);
      return newAutoPlay;
    });
  };

  const toggleKeyboardSound = () => {
    setKeyboardSound((prev) => {
      const newKeyboardSound = !prev;
      console.log(`键盘声音: ${newKeyboardSound}`);
      return newKeyboardSound;
    });
  };

  // App.js - 第5部分：用户进度加载
// 接着第4部分后面

  // === 优化的用户进度加载功能 ===
  const loadUserProgress = useCallback(
    async (userId, targetCategory) => {
      const maxRetries = 3;
      let attempt = 0;
      while (attempt < maxRetries) {
        try {
          console.log(`尝试加载用户 ${userId} 的 ${targetCategory} 进度, 尝试=${attempt + 1}`);
          const progressRef = doc(db, 'users', userId, 'progress', targetCategory);
          const progressDoc = await getDoc(progressRef);

          const cumulativeMasteredRef = doc(db, 'users', userId, 'cumulativeMastered', targetCategory);
          const cumulativeMasteredDoc = await getDoc(cumulativeMasteredRef);

          let progressData = {};
          let masteredData = {};
          let manualMasteredData = {};
          let learnedMasteredData = {};
          let lastReviewedWord = null;
          let savedIndex = 0;
          let inputWords = 0;
          let totalWords = words.filter(
            (w) => String(w.category || '').toLowerCase().trim() === targetCategory.toLowerCase().trim()
          ).length;

          let allWordsInCategory = words.filter((w) => {
            if (Array.isArray(w.category)) {
              return w.category.some(cat => 
                String(cat).toLowerCase().trim() === targetCategory.toLowerCase().trim()
              );
            }
            const wordCategory = String(w.category || '').toLowerCase().trim();
            const targetCategoryLower = targetCategory.toLowerCase().trim();
            return wordCategory === targetCategoryLower;
          });

          let cumulativeManualMastered = {};
          let cumulativeLearnedMastered = {};
          if (cumulativeMasteredDoc.exists()) {
            const cumulativeData = cumulativeMasteredDoc.data();
            cumulativeManualMastered = cumulativeData.manualMastered || {};
            cumulativeLearnedMastered = cumulativeData.learnedMastered || {};
            console.log('加载累积削词数据:', {
              手动削词: Object.keys(cumulativeManualMastered).length,
              学习掌握: Object.keys(cumulativeLearnedMastered).length,
            });
          }

          if (progressDoc.exists()) {
            const data = progressDoc.data();
            progressData = data.words || {};
            lastReviewedWord = data.lastReviewedWord || null;
            savedIndex = data.currentWordIndex || 0;
            inputWords = data.inputWords || 0;
            totalWords = data.totalWords || totalWords;
            console.log(`加载进度数据: lastReviewedWord=${lastReviewedWord}, currentWordIndex=${savedIndex}, inputWords=${inputWords}`);
          } else {
            console.log(`无 ${targetCategory} 进度数据，初始化 inputWords`);
            inputWords = allWordsInCategory.length;
          }

          const mergedMasteredWords = new Set();
          Object.entries(progressData).forEach(([word, progress]) => {
            if (progress.mastered && !mergedMasteredWords.has(word)) {
              masteredData[word] = true;
              mergedMasteredWords.add(word);
              if (progress.manualMaster) {
                manualMasteredData[word] = true;
              } else if (progress.reviewCount >= 6) {
                learnedMasteredData[word] = true;
              }
            }
          });

          Object.keys(cumulativeManualMastered).forEach((word) => {
            if (!mergedMasteredWords.has(word)) {
              masteredData[word] = true;
              manualMasteredData[word] = true;
              mergedMasteredWords.add(word);
            }
          });

          Object.keys(cumulativeLearnedMastered).forEach((word) => {
            if (!mergedMasteredWords.has(word)) {
              masteredData[word] = true;
              learnedMasteredData[word] = true;
              mergedMasteredWords.add(word);
            }
          });

          setDailyProgress(progressData);
          updateMasteredState({
            mastered: masteredData,
            manual: manualMasteredData,
            learned: learnedMasteredData
          });

          const unmastered = allWordsInCategory.filter((w) => !masteredData[w.word]);
          // 乱序处理
          const shuffledUnmastered = shuffleArray(unmastered);
          const finalInputWords = inputWords > 0 && inputWords <= shuffledUnmastered.length ? inputWords : shuffledUnmastered.length;
          const finalFilteredWords = shuffledUnmastered.slice(0, finalInputWords);

          setFilteredWords(finalFilteredWords);

          if (finalFilteredWords.length === 0) {
            setError(null);
            setCurrentWordIndex(0);
            console.log('无未掌握单词，设置索引为 0');
          } else {
            if (
              typeof savedIndex === 'number' &&
              savedIndex >= 0 &&
              savedIndex < finalFilteredWords.length &&
              finalFilteredWords[savedIndex].word === lastReviewedWord
            ) {
              setCurrentWordIndex(savedIndex);
              console.log(`恢复保存的索引: ${savedIndex}, 对应单词: ${finalFilteredWords[savedIndex]?.word}`);
            } else {
              const lastWordIndex = lastReviewedWord
                ? finalFilteredWords.findIndex((w) => w.word === lastReviewedWord)
                : -1;
              if (lastWordIndex !== -1) {
                setCurrentWordIndex(lastWordIndex);
                console.log(`找到最后学习的单词: ${lastReviewedWord}, 设置索引为: ${lastWordIndex}`);
              } else {
                const nextIndex = Math.min(savedIndex, finalFilteredWords.length - 1);
                setCurrentWordIndex(nextIndex);
                console.log(
                  `最后学习的单词 ${lastReviewedWord || '无'} 未找到或已掌握，设置索引为: ${nextIndex}, 单词: ${
                    finalFilteredWords[nextIndex]?.word || '无'
                  }`
                );
              }
            }
          }

          const totalCumulativeMasteredCount = Object.keys(manualMasteredData).length + Object.keys(learnedMasteredData).length;
          console.log(
            `类别总单词数: ${allWordsInCategory.length}, 未掌握: ${unmastered.length}, 当前学习: ${finalFilteredWords.length}, 累积削词总数: ${totalCumulativeMasteredCount}`
          );

          return { progressData, inputWords: finalInputWords, totalWords };
        } catch (err) {
          attempt++;
          console.error(`加载用户进度失败: 尝试=${attempt}/${maxRetries}`, {
            code: err.code,
            message: err.message,
            userId,
            targetCategory,
          });
          if (attempt === maxRetries) {
            setError(`加载用户进度失败: ${err.message}`);
            return {};
          }
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }
    },
    [words, updateMasteredState, shuffleArray]
  );

  // App.js - 第6部分：累积数据管理和级别管理
// 接着第5部分后面

  // === 累积数据管理 ===
  // ✅ 修改后
  const saveCumulativeMasteredWords = useCallback(
    async (targetCategory, manualMastered) => {  // ← 删除 learnedMastered 参数
      if (!user) {
        console.log('未登录，跳过保存累积削词数据');
        return;
      }

      try {
        console.log(
          `保存累积削词数据: 级别=${targetCategory}, 手动削词=${Object.keys(manualMastered).length}`
        );

        const cumulativeMasteredRef = doc(db, 'users', user.uid, 'cumulativeMastered', targetCategory);
        const docSnap = await getDoc(cumulativeMasteredRef);

        let existingManualMastered = {};
        // ✅ 删除 existingLearnedMastered

        if (docSnap.exists()) {
          const existingData = docSnap.data();
          existingManualMastered = existingData.manualMastered || {};
          // ✅ 删除读取 learnedMastered 的代码
        }

        const updatedManualMastered = { ...existingManualMastered };
        Object.keys(manualMastered).forEach((word) => {
          if (!updatedManualMastered[word]) {
            updatedManualMastered[word] = true;
          }
        });

        // ✅ 删除 updatedLearnedMastered 相关代码

        const cumulativeData = {
          manualMastered: updatedManualMastered,
          learnedMastered: {},  // ✅ 保留空对象，避免数据库报错
          lastUpdated: Date.now(),
        };

        await setDoc(cumulativeMasteredRef, cumulativeData);
        console.log(`成功保存累积削词数据: 级别=${targetCategory}`, cumulativeData);
      } catch (err) {
        console.error(`保存累积削词数据失败: 级别=${targetCategory}`, err);
        setError(`保存累积削词数据失败: ${err.message}`);
      }
    },
    [user]
  );

  const loadCompletedCounts = useCallback(
    async (userId) => {
      const maxRetries = 3;
      let attempt = 0;
      while (attempt < maxRetries) {
        try {
          console.log(`尝试加载用户 ${userId} 的完成次数, 尝试=${attempt + 1}`);
          const completedCountsRef = doc(db, 'users', userId, 'completedCounts', 'counts');
          const completedCountsDoc = await getDoc(completedCountsRef);
          if (completedCountsDoc.exists()) {
            setCompletedCounts(completedCountsDoc.data());
            console.log('加载完成次数:', completedCountsDoc.data());
            return;
          } else {
            const initialCounts = {};
            categories.forEach((cat) => {
              initialCounts[cat.value] = 0;
            });
            await setDoc(completedCountsRef, initialCounts);
            setCompletedCounts(initialCounts);
            console.log('初始化完成次数:', initialCounts);
            return;
          }
        } catch (err) {
          attempt++;
          console.error(`加载完成次数失败: 尝试=${attempt}/${maxRetries}`, {
            code: err.code,
            message: err.message,
            userId,
          });
          if (attempt === maxRetries) {
            setError(`加载完成次数失败: ${err.message}`);
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }
    },
    [categories]
  );

  // === 优化的级别切换功能 ===
  const handleCategoryChange = useCallback(
      async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const newCategory = e.target.value;
      if (newCategory === category) return;

      // ✅ 确保这段代码在这里
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
        console.log('切换级别前停止音频播放');
      }

      if (filteredWords.length === 0) {
        console.log(`当前级别已完成，直接切换到: ${newCategory}`);
        setDailyProgress({});
        updateMasteredState({
          mastered: {},
          manual: {},
          learned: {}
        });
        setCurrentWordIndex(0);
        setInputValue('');
        setError(null);
        setCategory(newCategory);
        // 保存到 localStorage
        localStorage.setItem('lastCategory', newCategory);
        if (user) {
          await loadUserProgress(user.uid, newCategory);
        } else {
          setFilteredWords([]);
        }
        if (inputRef.current && e.target.tagName !== 'SELECT') {
          inputRef.current.focus();
          console.log('级别切换后输入框聚焦');
        }
        return;
      }

      console.log(`选择级别: ${newCategory}`);
      try {
        if (user) {
          const saveSuccess = await saveAllProgress(category);
          if (!saveSuccess) {
            setError('保存当前级别进度失败，请重试');
            return;
          }
        }
        setDailyProgress({});
        updateMasteredState({
          mastered: {},
          manual: {},
          learned: {}
        });
        setCurrentWordIndex(0);
        setInputValue('');
        setError(null);
        setCategory(newCategory);
        // 保存到 localStorage
        localStorage.setItem('lastCategory', newCategory);
        if (user) {
          await loadUserProgress(user.uid, newCategory);
        } else {
          setFilteredWords([]);
        }
        if (inputRef.current && e.target.tagName !== 'SELECT') {
          inputRef.current.focus();
          console.log('级别切换后输入框聚焦');
        }
      } catch (err) {
        console.error('级别切换失败:', err);
        setError(`级别切换失败: ${err.message}`);
      }
    },
    [user, category, saveAllProgress, loadUserProgress, filteredWords, updateMasteredState]
  );

  // === 登出功能 ===
  const handleLogout = async () => {
    try {
      if (user && (filteredWords.length > 0 || Object.keys(dailyProgress).length > 0)) {
        console.log('登出前保存完整进度...');
        const saveSuccess = await saveAllProgress(category);
        if (!saveSuccess) {
          setError('保存进度失败，请重试后再登出');
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      await signOut(auth);
      console.log('登出成功');
      setDailyProgress({});
      updateMasteredState({
        mastered: {},
        manual: {},
        learned: {}
      });
      setCompletedCounts({});
      setCurrentWordIndex(0);

      const filtered = words.filter((w) => {
        if (Array.isArray(w.category)) {
          return w.category.some(cat => 
            String(cat).toLowerCase().trim() === category.toLowerCase().trim()
          );
        }
        return String(w.category || '').toLowerCase().trim() === category.toLowerCase().trim();
      });
      const shuffledFiltered = shuffleArray(filtered);
      setFilteredWords(shuffledFiltered);

      if (filtered.length === 0 && words.length > 0) {
        setError(`无 ${category} 级别的未掌握单词`);
      }
    } catch (err) {
      console.error('登出失败:', err, { code: err.code, message: err.message });
      setError('登出失败: ' + err.message);
    }
  };

  // App.js - 第7部分：级别完成和重置功能
// 接着第6部分后面

  // === 级别完成功能 ===
  const completeCurrentCategory = useCallback(
    async () => {
      if (!user) {
        setError('请先登录');
        return;
      }

      try {
        console.log(`完成 ${category} 级别学习，增加完成次数并保存累积削词数据`);

        await saveCumulativeMasteredWords(category, masteredState.manual);  // ✅ 删除第三个参数

        const completedCountsRef = doc(db, 'users', user.uid, 'completedCounts', 'counts');
        const completedCountsDoc = await getDoc(completedCountsRef);
        let newCompletedCounts;

        if (completedCountsDoc.exists()) {
          const existingCounts = completedCountsDoc.data();
          newCompletedCounts = {
            ...existingCounts,
            [category]: (existingCounts[category] || 0) + 1,
          };
        } else {
          newCompletedCounts = {};
          categories.forEach((cat) => {
            newCompletedCounts[cat.value] = cat.value === category ? 1 : 0;
          });
        }

        await setDoc(completedCountsRef, newCompletedCounts);
        setCompletedCounts(newCompletedCounts);

        setDailyProgress({});
        updateMasteredState({
          mastered: {},
          manual: {},
          learned: {}
        });
        setCurrentWordIndex(0);
        setError(null);

        const progressRef = doc(db, 'users', user.uid, 'progress', category);
        const totalWords = words.filter(
          (w) => String(w.category || '').toLowerCase().trim() === category.toLowerCase().trim()
        ).length;
        await setDoc(progressRef, {
          words: {},
          lastReviewedWord: null,
          inputWords: totalWords,
          totalWords,
          lastUpdated: Date.now(),
        }, { merge: true });

        await loadUserProgress(user.uid, category);

        const unmastered = words.filter((w) => {
          let match = false;
          if (Array.isArray(w.category)) {
            match = w.category.some(cat => 
              String(cat).toLowerCase().trim() === category.toLowerCase().trim()
            );
          } else {
            const wordCategory = String(w.category || '').toLowerCase().trim();
            match = wordCategory === category.toLowerCase().trim();
          }
          return match && !masteredState.mastered[w.word];
        });
        const shuffledUnmastered = shuffleArray(unmastered);
        setFilteredWords(shuffledUnmastered);

        if (inputRef.current) {
          inputRef.current.focus();
        }

        console.log(`完成 ${category} 级别学习成功，完成次数: ${newCompletedCounts[category]}`);
      } catch (err) {
        console.error('完成级别学习失败:', err, { code: err.code, message: err.message });
        setError(`完成级别学习失败: ${err.message}`);
      }
    },
    [user, category, saveCumulativeMasteredWords, masteredState.manual, masteredState.mastered, updateMasteredState, words, loadUserProgress, shuffleArray, categories]
  );

  const getCumulativeMasteredWords = useCallback(
    async (targetCategory) => {
      if (!user) return { manualMastered: {}, learnedMastered: {} };

      const maxRetries = 3;
      let attempt = 0;
      while (attempt < maxRetries) {
        try {
          console.log(`获取 ${targetCategory} 的累积削词列表, 尝试=${attempt + 1}`);

          const cumulativeMasteredRef = doc(db, 'users', user.uid, 'cumulativeMastered', targetCategory);
          const cumulativeMasteredDoc = await getDoc(cumulativeMasteredRef);

          let cumulativeManualMastered = {};
          let cumulativeLearnedMastered = {};

          if (cumulativeMasteredDoc.exists()) {
            const cumulativeData = cumulativeMasteredDoc.data();
            cumulativeManualMastered = cumulativeData.manualMastered || {};
            cumulativeLearnedMastered = cumulativeData.learnedMastered || {};
          }

          const progressRef = doc(db, 'users', user.uid, 'progress', targetCategory);
          const progressDoc = await getDoc(progressRef);

          if (progressDoc.exists()) {
            const data = progressDoc.data();
            const progressData = data.words || {};

            Object.entries(progressData).forEach(([word, progress]) => {
              if (progress.mastered) {
                if (progress.manualMaster) {
                  cumulativeManualMastered[word] = true;
                } else if (progress.reviewCount >= 6) {
                  cumulativeLearnedMastered[word] = true;
                }
              }
            });
          }

          console.log(
            `累积削词统计 - ${targetCategory}: 手动削词=${Object.keys(cumulativeManualMastered).length}, 学习掌握=${Object.keys(
              cumulativeLearnedMastered
            ).length}`
          );

          return {
            manualMastered: cumulativeManualMastered,
            learnedMastered: cumulativeLearnedMastered,
          };
        } catch (err) {
          attempt++;
          console.error(`获取累积削词列表失败: ${targetCategory}, 尝试=${attempt}/${maxRetries}`, {
            code: err.code,
            message: err.message,
          });
          if (attempt === maxRetries) {
            setError(`获取累积削词列表失败: ${err.message}`);
            return { manualMastered: {}, learnedMastered: {} };
          }
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }
    },
    [user]
  );

  // === 重置类别功能 ===
  const resetCategory = useCallback(
    async (categoryToReset) => {
      if (!user) {
        setError('请先登录');
        return;
      }

      try {
        console.log(`重置类别: ${categoryToReset}`);
        
        const progressRef = doc(db, 'users', user.uid, 'progress', categoryToReset);
        await setDoc(progressRef, {
          words: {},
          lastReviewedWord: null,
          currentWordIndex: 0,
          inputWords: 0,
          totalWords: words.filter(
            (w) => String(w.category || '').toLowerCase().trim() === categoryToReset.toLowerCase().trim()
          ).length,
          lastUpdated: Date.now(),
        }, { merge: true });

        const cumulativeMasteredRef = doc(db, 'users', user.uid, 'cumulativeMastered', categoryToReset);
        await setDoc(cumulativeMasteredRef, {
          manualMastered: {},
          learnedMastered: {},
          lastUpdated: Date.now(),
        }, { merge: true });

        const completedCountsRef = doc(db, 'users', user.uid, 'completedCounts', 'counts');
        const completedCountsDoc = await getDoc(completedCountsRef);
        if (completedCountsDoc.exists()) {
          const existingCounts = completedCountsDoc.data();
          const newCompletedCounts = {
            ...existingCounts,
            [categoryToReset]: 0,
          };
          await setDoc(completedCountsRef, newCompletedCounts);
          setCompletedCounts(newCompletedCounts);
        }

        if (category === categoryToReset) {
          await loadUserProgress(user.uid, categoryToReset);
        }

        console.log(`类别 ${categoryToReset} 重置成功`);
      } catch (err) {
        console.error('重置类别失败:', err);
        setError(`重置失败: ${err.message}`);
      }
    },
    [user, category, words, loadUserProgress]
  );

  // 在 handleInputChange 函数开头添加字符标准化函数
const normalizeText = (text) => {
  return text
    .trim()
    .toLowerCase()
    // 将所有类型的引号/撇号统一为普通单引号
    .replace(/['']/g, "'")
    // 将所有类型的双引号统一
    .replace(/[""]/g, '"')
    // 移除零宽字符
    .replace(/[\u200B-\u200D\uFEFF]/g, '');
};

// 修改后的 handleInputChange 函数（关键部分）
const handleInputChange = useCallback(
  async (e) => {
    const rawInput = e.target.value;
    
    // ✅ 使用标准化函数处理输入
    const processedInput = normalizeText(rawInput);

    if (rawInput.length > inputValue.length && keyboardSound) {
      playKeySound();
    }

    setInputValue(rawInput);

    const currentWordData = filteredWords[currentWordIndex];
    if (!currentWordData || filteredWords.length === 0) return;

    // ===== 削词模式逻辑 =====
    if (uiState.isInMasterMode && uiState.masterModeWord === currentWordData.word) {
      // ✅ 标准化目标单词
      const targetWord = normalizeText(currentWordData.word);
      const trimmedInput = normalizeText(rawInput);
      
      // 检查输入是否完全正确
      if (trimmedInput === targetWord && trimmedInput.length === targetWord.length) {
        const wordToMaster = uiState.masterModeWord;
        if (wordToMaster) {
          console.log(`削词输入正确，准备执行削词：${wordToMaster}`);
          await executeMasterWord(wordToMaster);
        }
        return;
      }
      
      // 检查输入是否有错误
      if (trimmedInput.length > 0 && !targetWord.startsWith(trimmedInput)) {
        alert('输入有误，已退出削词模式');
        updateUiState({
          isInMasterMode: false,
          masterModeWord: '',
          masterModeFailedWords: {
            ...uiState.masterModeFailedWords,
            [currentWordData.word]: true
          }
        });
        setInputValue('');
        if (inputRef.current) {
          inputRef.current.focus();
        }
        return;
      }
      
      return;
    }

    // ===== 正常学习模式逻辑 =====
    if (inputTimerRef.current) {
      clearTimeout(inputTimerRef.current);
      inputTimerRef.current = null;
    }

    updateUiState({ isInputIncorrect: false });

    if (rawInput.trim().length > 0) {
      inputTimerRef.current = setTimeout(() => {
        if (currentWordData && rawInput.trim().length > 0) {
          // ✅ 标准化处理
          const targetWord = normalizeText(currentWordData.word);
          const targetChinese = currentWordData.meaning_cn 
            ? normalizeText(currentWordData.meaning_cn) 
            : '';

          const isExactEnglishMatch = 
            processedInput === targetWord && 
            processedInput.length === targetWord.length;
          const isExactChineseMatch = 
            targetChinese && 
            processedInput === targetChinese && 
            processedInput.length === targetChinese.length;
          const isCorrect = isExactEnglishMatch || isExactChineseMatch;

          if (!isCorrect) {
            updateUiState({ isInputIncorrect: true });
          }
        }
      }, 2000);
    }

    console.log(`输入: "${processedInput}", 单词: "${normalizeText(currentWordData.word)}"`);

    // ✅ 最终匹配判断
    const targetWord = normalizeText(currentWordData.word);
    const targetChinese = currentWordData.meaning_cn 
      ? normalizeText(currentWordData.meaning_cn) 
      : '';

    const isExactEnglishMatch = 
      processedInput === targetWord && 
      processedInput.length === targetWord.length;
    const isExactChineseMatch = 
      targetChinese && 
      processedInput === targetChinese && 
      processedInput.length === targetChinese.length;

    const isCorrect = isExactEnglishMatch || isExactChineseMatch;

    console.log(`标准化匹配结果: English=${isExactEnglishMatch}, Chinese=${isExactChineseMatch}, Final=${isCorrect}`);

    if (processedInput.length > 0 && !isCorrect) {
      updateUiState({ isIncorrectWord: true });
      setTimeout(() => updateUiState({ isIncorrectWord: false }), 1000);
    } else {
      updateUiState({ isIncorrectWord: false });
    }

      if (isCorrect) {
        updateUiState({ showLoading: false });
        const now = new Date().getTime();
        const progress = dailyProgress[currentWordData.word] || { reviewCount: 0, lastReviewed: 0, nextReview: 0 };
        const reviewCount = (progress.reviewCount || 0) + 1;
        const reviewIndex = Math.min(reviewCount, ebbinghausIntervals.length - 1);
        const nextReview = now + ebbinghausIntervals[reviewIndex] * 60 * 1000;

        const mastered = reviewCount >= 6;

        const nextDays = intervalDays[Math.min(reviewCount, intervalDays.length - 1)];
        if (!mastered && nextDays > 0) {
          console.log(`单词 ${currentWordData.word} 需要在 ${nextDays} 天后复习`);
        }

        const newFilteredWords = filteredWords.filter((w) => w.word !== currentWordData.word);
        const newInputWords = newFilteredWords.length;

        if (user) {
          const updatedDailyProgress = {
            ...dailyProgress,
            [currentWordData.word]: {
              reviewCount,
              lastReviewed: now,
              nextReview,
              mastered,
              manualMaster: false,
            },
          };

          setDailyProgress(updatedDailyProgress);

          if (mastered) {
            updateMasteredState({
              mastered: { ...masteredState.mastered, [currentWordData.word]: true },
              // learned: { ...masteredState.learned, [currentWordData.word]: true }
            });

            const cumulativeMasteredRef = doc(db, 'users', user.uid, 'cumulativeMastered', category);
            const docSnap = await getDoc(cumulativeMasteredRef);
            let cumulativeManualMastered = docSnap.exists() ? docSnap.data().manualMastered || {} : {};
            // let cumulativeLearnedMastered = docSnap.exists() ? docSnap.data().learnedMastered || {} : {};

            if (!cumulativeManualMastered[currentWordData.word]) {
              // cumulativeLearnedMastered[currentWordData.word] = true;
            }

            await setDoc(
              cumulativeMasteredRef,
              {
                manualMastered: cumulativeManualMastered,
                learnedMastered: {},  // 改为 {}
                lastUpdated: Date.now(),
              },
              { merge: true }
            );
          }

          await saveProgress(currentWordData.word, reviewCount, now, nextReview, mastered, newInputWords);
        }

        setFilteredWords(newFilteredWords);
        setInputValue('');
        
        // 清除削词失败记录
        if (uiState.masterModeFailedWords[currentWordData.word]) {
          const newFailedWords = { ...uiState.masterModeFailedWords };
          delete newFailedWords[currentWordData.word];
          updateUiState({ masterModeFailedWords: newFailedWords });
        }
        
        if (newFilteredWords.length > 0) {
          setCurrentWordIndex((prev) => (prev < newFilteredWords.length ? prev : 0));
        } else {
          setCurrentWordIndex(0);
          if (words.length > 0) {
            setError(`无 ${category} 类别的未掌握单词`);
          }
        }

        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    },
    [
      currentWordIndex,
      filteredWords,
      dailyProgress,
      saveProgress,
      user,
      category,
      words,
      inputValue.length,
      keyboardSound,
      playKeySound,
      updateUiState,
      
      masteredState.mastered,
      // masteredState.manual,
      updateMasteredState,
      uiState.isInMasterMode,
      uiState.masterModeWord,
      uiState.masterModeFailedWords,
      executeMasterWord,
    ]
  );

  // === 事件处理函数 ===
  const handleInputBlur = useCallback(
    (e) => {
      const now = Date.now();
      if (now - lastBlurTimeRef.current < 200) {
        console.log('忽略快速重复失焦事件');
        return;
      }
      lastBlurTimeRef.current = now;

      if (
        e.relatedTarget &&
        (e.relatedTarget.tagName === 'SELECT' ||
          emailInputRef.current?.contains(e.relatedTarget) ||
          passwordInputRef.current?.contains(e.relatedTarget))
      ) {
        console.log('忽略由 select 或登录输入框触发的失焦事件');
        updateUiState({ isWordInputFocused: false });
        return;
      }

      if (inputRef.current && uiState.isWordInputFocused && !document.activeElement?.tagName.startsWith('INPUT')) {
        console.log('单词输入框失去焦点，触发重新聚焦');
        blurTimeoutRef.current = setTimeout(() => {
          if (inputRef.current && !document.activeElement?.tagName.startsWith('INPUT')) {
            inputRef.current.focus();
            console.log('单词输入框重新聚焦');
          }
        }, 200);
      }
    },
    [uiState.isWordInputFocused, updateUiState]
  );

  const handleLoginFocus = () => {
    updateUiState({ isWordInputFocused: false });
    console.log('登录/注册输入框聚焦，禁用单词输入框自动聚焦');
  };

  const handleSelectEvent = useCallback((e) => {
    e.stopPropagation();
    console.log(`select 事件: ${e.type}`);
  }, []);

  const handleFirstInteraction = useCallback(() => {
    if (!interaction) {
      setInteraction(true);
      console.log('首次交互，启用自动播放');
      
      // 立即尝试播放当前单词
      if (autoPlay && filteredWords.length > 0 && currentWordIndex < filteredWords.length) {
        const currentWordData = filteredWords[currentWordIndex];
        if (currentWordData.audio?.[pronunciationType] && currentWordData.audio[pronunciationType] !== '') {
          setTimeout(() => {
            playAudio(currentWordData.audio[pronunciationType], currentWordData.word, false);
          }, 100);
        }
      }
    }
  }, [interaction, autoPlay, filteredWords, currentWordIndex, pronunciationType, playAudio]);

  // 快捷键处理函数
  const handleKeyDown = useCallback(
    (e) => {
      // Ctrl + X: 进入削词模式
      if (e.ctrlKey && e.key.toLowerCase() === 'x') {
        e.preventDefault();
        if (user && filteredWords.length > 0 && currentWordIndex < filteredWords.length) {
          const currentWordData = filteredWords[currentWordIndex];
          handleManualMaster(currentWordData.word);
        }
        return;
      }
      
      // Ctrl + C: 退出削词模式
      if (e.ctrlKey && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        if (uiState.isInMasterMode) {
          updateUiState({
            isInMasterMode: false,
            masterModeWord: ''
          });
          setInputValue('');
          if (inputRef.current) {
            inputRef.current.focus();
          }
          console.log('快捷键退出削词模式');
        }
        return;
      }
    },
    [user, filteredWords, currentWordIndex, handleManualMaster, uiState.isInMasterMode, updateUiState]
  );

  // === 键盘事件处理 ===
  const handleKeyPressToRestart = useCallback(
    (e) => {
      // 当filteredWords为空且有学习记录时，表示本次学习完成
      if (filteredWords.length === 0 && Object.keys(dailyProgress).length > 0 && user) {
        console.log('检测到按键，完成当前级别学习:', e.key);
        completeCurrentCategory();
      }
    },
    [filteredWords.length, dailyProgress, user, completeCurrentCategory]
  );

  // App.js - 第9部分：认证功能和useEffect
// 接着第8部分后面

  // === 认证功能 ===
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email) {
      setAuthError('请填邮箱地址');
      return;
    }
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await userCredential.user.getIdToken(true);
      console.log('登录成功:', email, { uid: userCredential.user.uid });
      setEmail('');
      setPassword('');
      setAuthError(null);
    } catch (err) {
      console.error('登录失败:', err, { code: err.code, message: err.message });
      if (err.code === 'auth/invalid-email') {
        setAuthError('请填邮箱地址');
      } else {
        setAuthError('登录失败: ' + err.message);
      }
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!email) {
      setAuthError('请填邮箱地址');
      return;
    }
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('注册成功:', email, { uid: userCredential.user.uid });
      setEmail('');
      setPassword('');
      setAuthError(null);
    } catch (err) {
      console.error('注册失败:', err, { code: err.code, message: err.message });
      if (err.code === 'auth/invalid-email') {
        setAuthError('请填邮箱地址');
      } else {
        setAuthError('注册失败: ' + err.message);
      }
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      setAuthError('请填写邮箱地址');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      console.log('密码重置邮件已发送:', email);
      setAuthError('密码重置邮件已发送，请检查您的邮箱');
      setEmail('');
      setPassword('');
    } catch (err) {
      console.error('密码重置失败:', err, { code: err.code, message: err.message });
      if (err.code === 'auth/invalid-email') {
        setAuthError('请填写有效的邮箱地址');
      } else if (err.code === 'auth/user-not-found') {
        setAuthError('该邮箱未注册');
      } else {
        setAuthError('密码重置失败: ' + err.message);
      }
    }
  };

  const filterWords = useCallback(
    () => {
      console.log(`开始过滤单词，当前级别: ${category}`);

      const filtered = words.filter((w) => {
        let match = false;
        if (Array.isArray(w.category)) {
          match = w.category.some(cat => 
            String(cat).toLowerCase().trim() === category.toLowerCase().trim()
          );
        } else {
          const wordCategory = String(w.category || '').toLowerCase().trim();
          match = wordCategory === category.toLowerCase().trim();
        }
        
        const notMastered = !masteredState.mastered[w.word];

        if (match && notMastered) {
          const reviewNeeded = needsReview(w.word);
          return reviewNeeded;
        }

        return false;
      });

      // 乱序处理
      const shuffled = shuffleArray(filtered);

      console.log(`过滤后单词数: ${shuffled.length}, 级别: ${category}`);
      setFilteredWords(shuffled);
      if (shuffled.length === 0 && words.length > 0) {
        setError(`无 ${category} 级别的未掌握单词`);
        setCurrentWordIndex(0);
      } else {
        setError(null);
      }
      if (inputRef.current) {
        inputRef.current.focus();
        console.log('级别切换后输入框聚焦');
      }
    },
    [category, words, masteredState.mastered, needsReview, shuffleArray]
  );

  // === useEffect 钩子 ===

  // 👇 新增：监听屏幕尺寸变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (inputTimerRef.current) {
        clearTimeout(inputTimerRef.current);
      }
    };
  }, []);

  // 重置输入状态
  useEffect(() => {
    updateUiState({ isInputIncorrect: false });
    if (inputTimerRef.current) {
      clearTimeout(inputTimerRef.current);
      inputTimerRef.current = null;
    }
  }, [currentWordIndex, updateUiState]);

  // 优化的自动播放useEffect
  useEffect(() => {
    if (
      currentPage === 'learning' &&
      !uiState.searchMode &&
      filteredWords.length > 0 &&
      currentWordIndex < filteredWords.length &&
      autoPlay &&
      interaction &&
      !loopPlay
    ) {
      const currentWordData = filteredWords[currentWordIndex];
      if (currentWordData.audio?.[pronunciationType] && currentWordData.audio[pronunciationType] !== '') {
        const timer = setTimeout(() => {
          // ✅ 播放前先停止所有音频
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = '';
            audioRef.current = null;
          }
          
          // ✅ 创建新的音频实例并保存到 ref
          const audio = new Audio(currentWordData.audio[pronunciationType]);
          audioRef.current = audio;  // 保存引用
          
          const playPromise = audio.play();
          
          if (playPromise !== undefined) {
            playPromise
              .then(() => {
                console.log(`自动播放成功: ${currentWordData.word}`);
              })
              .catch((err) => {
                console.warn(`自动播放被阻止: ${err.message}`);
              });
          }
        }, 300);
        
        // ✅ 清理函数：停止定时器和音频
        return () => {
          clearTimeout(timer);
          if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.src = '';
            audioRef.current = null;
          }
        };
      } else {
        console.log(`无音频可播放: ${currentWordData.word}`);
      }
    }
  }, [currentWordIndex, filteredWords, autoPlay, interaction, loopPlay, currentPage, pronunciationType, uiState.searchMode]);

  // 优化的加载状态useEffect
  useEffect(() => {
    updateUiState({ showLoading: false });
        
    if (!currentWord || !inputValue.trim()) return;

    const isComplete =
      inputValue.trim().toLowerCase() === currentWord.word?.toLowerCase() ||
      (currentWord.meaning_cn && inputValue.trim().toLowerCase() === currentWord.meaning_cn.toLowerCase());

    if (isComplete) {
      const timer = setTimeout(() => {
        updateUiState({ showLoading: true });
      }, 2000);
      // return () => clearTimeout(timer); // 这行是否修改有待确认
      return () => {
        clearTimeout(timer);
        // 清理时也停止音频
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = '';
          audioRef.current = null;
        }
      };
    }
  }, [inputValue, currentWord, updateUiState]);

  // 认证状态监听
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        console.log('用户已登录:', currentUser.email, { uid: currentUser.uid });
        setUserProfile({
          displayName: currentUser.displayName || '',
          photoURL: currentUser.photoURL || '',
        });
        try {
          const testRef = doc(db, 'users', currentUser.uid, 'test', 'permissions');
          await setDoc(testRef, { test: 'write', timestamp: Date.now() }, { merge: true });
          console.log('测试写入成功');
          const docSnap = await getDoc(testRef);
          if (docSnap.exists()) {
            console.log('测试读取成功:', docSnap.data());
          } else {
            console.log('测试文档不存在');
          }
        } catch (err) {
          console.error('测试 Firestore 访问失败:', err, { code: err.code, message: err.message });
        }
        await loadUserProgress(currentUser.uid, category);
        await loadCompletedCounts(currentUser.uid);
      } else {
        console.log('用户未登录');
        setUserProfile({ displayName: '', photoURL: '' });
        setDailyProgress({});
        updateMasteredState({
          mastered: {},
          manual: {},
          learned: {}
        });
        setCompletedCounts({});
        setCurrentWordIndex(0);
        const filtered = words.filter((w) => {
          if (Array.isArray(w.category)) {
            return w.category.some(cat => 
              String(cat).toLowerCase().trim() === category.toLowerCase().trim()
            );
          }
          return String(w.category || '').toLowerCase().trim() === category.toLowerCase().trim();
        });
        const shuffledFiltered = shuffleArray(filtered);
        setFilteredWords(shuffledFiltered);
        if (filtered.length === 0 && words.length > 0) {
          setError(`无 ${category} 类别的单词`);
        } else {
          setError(null);
        }
      }
    });
    return () => unsubscribe();
  }, [category, loadUserProgress, loadCompletedCounts, words, updateMasteredState, shuffleArray]);

  // App.js - 第10部分：其余useEffect和渲染部分
// 接着第9部分后面，这是最后一部分

  // 加载单词数据
  useEffect(() => {
    console.log('开始加载 words.json');
    const fetchWords = async () => {
      try {
        const response = await fetch('/words.json');
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('单词文件未找到 (404)。请确保 words.json 存在于 public 文件夹中。');
          } else if (!navigator.onLine) {
            throw new Error('网络连接断开。请检查您的网络设置。');
          } else {
            throw new Error(`HTTP 错误: ${response.status} ${response.statusText}`);
          }
        }
        const data = await response.json();
        console.log('加载 words.json 成功，单词数:', data.length);
        setWords(data);
        if (!user) {
          const filtered = data.filter((w) => {
            if (Array.isArray(w.category)) {
              return w.category.some(cat => 
                String(cat).toLowerCase().trim() === category.toLowerCase().trim()
              );
            }
            return String(w.category || '').toLowerCase().trim() === category.toLowerCase().trim();
          });
          const shuffledFiltered = shuffleArray(filtered);
          setFilteredWords(shuffledFiltered);

          if (filtered.length === 0 && data.length > 0) {
            setError(`无 ${category} 类别的未掌握单词`);
          } else {
            setError(null);
          }
        }
      } catch (error) {
        console.error('加载 words.json 失败:', error, {
          message: error.message,
          stack: error.stack,
        });
        setError(`数据加载失败: ${error.message}`);
      }
    };

    fetchWords();
  }, [category, shuffleArray, user]);

  // 过滤单词
  useEffect(() => {
    if (words.length > 0 && !user) {
      filterWords();
    }
  }, [category, words, masteredState.mastered, filterWords, user]);

  // 键盘事件监听
  useEffect(() => {
    // 添加快捷键监听
    document.addEventListener('keydown', handleKeyDown);
    
    if (filteredWords.length === 0 && Object.keys(dailyProgress).length > 0 && user) {
      console.log('添加键盘事件监听器');
      document.addEventListener('keydown', handleKeyPressToRestart);

      return () => {
        console.log('移除键盘事件监听器');
        document.removeEventListener('keydown', handleKeyPressToRestart);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [filteredWords.length, dailyProgress, user, handleKeyPressToRestart, handleKeyDown]);

  // 页面卸载处理
  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      if (user && Object.keys(dailyProgress).length > 0) {
        console.log('页面卸载，保存进度到 Firebase');
        event.preventDefault();
        event.returnValue = '';
        
        try {
          await saveAllProgress(category);
        } catch (err) {
          console.error('页面卸载时保存失败:', err);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [user, category, dailyProgress, saveAllProgress]);

  // 首次交互监听
  useEffect(() => {
    document.addEventListener('click', handleFirstInteraction);
    return () => document.removeEventListener('click', handleFirstInteraction);
  }, [handleFirstInteraction]);

  // 页面标题设置
  useEffect(() => {
    const categoryLabels = {
      elementary: '小学 (300-1000 词)',
      junior: '初中 (1000-1500 词)',
      highschool: '高中 (1500-3500 词)',
      cet4: '四级 (~4500 词)',
      cet6: '六级 (~6000 词)',
      toefl: '考研/托福 (~8000-10000 词)',
      ielts_gre: '雅思/GRE (~10000-15000 词)',
    };
    
    // 只修改 title，不修改 description
    document.title = `削词侠 - ${categoryLabels[category] || '英语词汇学习'}`;
  }, [category]);

  // === 渲染部分 ===
  // About页面渲染
  if (currentPage === 'about') {
    return <About onBack={() => setCurrentPage('learning')} />;
  }

  // Contact页面渲染
  if (currentPage === 'contact') {
    return <Contact onBack={() => setCurrentPage('learning')} />;
  }

  // PrivacyPolicy页面渲染
  if (currentPage === 'privacy') {
    return <PrivacyPolicy onBack={() => setCurrentPage('learning')} />;
  }
  // PersonalCenter页面渲染
  if (currentPage === 'personal' && user) {
    return (
      <Suspense fallback={<div>加载中...</div>}>
        <PersonalCenter
          user={user}
          categories={categories}
          completedCounts={completedCounts}
          words={words}
          onBack={() => setCurrentPage('learning')}
          getCumulativeMasteredWords={getCumulativeMasteredWords}
          userProfile={userProfile}
          setUserProfile={setUserProfile}
          onResetCategory={resetCategory}
        />
      </Suspense>
    );
  }

  // === 主题颜色定义 ===
  const themeColors = {
    light: {
      background: '#ffffff',
      text: '#000000',
      secondaryText: '#666666',
      border: '#000000',
      cardBg: '#f9f9f9',
      inputBorder: '#ddd',
      errorBg: '#ffe6e6',
      errorText: 'red',
    },
    dark: {
      background: '#1a1a1a',
      text: '#ffffff',
      secondaryText: '#cccccc',
      border: '#ffffff',
      cardBg: '#2a2a2a',
      inputBorder: '#555',
      errorBg: '#4a1a1a',
      errorText: '#ff6b6b',
    }
  };

  const currentTheme = themeColors[theme];

  // 👇 新增：响应式样式定义（在最后的 return 之前）
  const containerStyle = {
    padding: isMobile ? '10px' : '20px',
    width: '100%',  // 改为100%
    margin: '0',    // 改为0
    userSelect: 'text',
    maxWidth: 'none',  // 移除最大宽度限制
    backgroundColor: currentTheme.background,
    color: currentTheme.text,
    minHeight: '100vh',
    transition: 'background-color 0.3s, color 0.3s',
    boxSizing: 'border-box',  // 添加这一行
  };

  const headerStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'space-between',
    alignItems: isMobile ? 'center' : 'flex-start',
    marginBottom: '20px',
    gap: isMobile ? '15px' : '0',
  };

  const titleSectionStyle = {
    width: isMobile ? '100%' : '20%',
    textAlign: isMobile ? 'center' : 'left',
  };

  const centerSectionStyle = {
    width: isMobile ? '100%' : '60%',
    textAlign: 'center',
  };

  const userSectionStyle = {
    width: isMobile ? '100%' : '20%',
    textAlign: isMobile ? 'center' : 'right',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    minHeight: isMobile ? 'auto' : '200px',
    paddingBottom: '5px',
  };

  const wordDisplayStyle = {
    fontSize: isMobile ? '2em' : '3em',
    margin: '10px 0',
    letterSpacing: '0.05em',
    wordBreak: 'break-word',
  };

  // const inputWrapperStyle = {
  //   display: 'flex',
  //   alignItems: 'center',
  //   justifyContent: 'center',
  //   position: 'relative',
  //   width: '100%',
  //   maxWidth: isMobile ? '300px' : 'none',
  // };

  const inputFieldStyle = {
    border: 'none',
    // borderBottom: `2px solid ${currentTheme.border}`,
    borderBottom: '2px solid #3498db', // 改为蓝色下划线
    borderRadius: '0',
    width: isMobile ? '100%' : `${(currentWord.word?.length || 10) * 1.5}em`,
    maxWidth: isMobile ? '300px' : 'none',
    fontSize: isMobile ? '1.2em' : '1.5em',
    textAlign: 'center',
    outline: 'none',
    paddingRight: '0',  // 改为 '0'（已经是正确的）
    color: uiState.isInputIncorrect ? '#3498db' : currentTheme.text,
    backgroundColor: currentTheme.background,
  };

  const buttonContainerStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: 'center',
    gap: isMobile ? '10px' : '20px',
    justifyContent: 'center',
    marginTop: '10px',
  };

  const masterButtonStyle = {
    padding: isMobile ? '10px 15px' : '8px 20px',
    background: 
      user && 
      !uiState.isMasterLoading && 
      !uiState.masterModeFailedWords[currentWord.word]
        ? (uiState.isInMasterMode ? '#52c41a' : '#1890ff')
        : '#d9d9d9',
    color: 
      user && 
      !uiState.isMasterLoading && 
      !uiState.masterModeFailedWords[currentWord.word]
        ? 'white' 
        : '#999',
    border: 'none',
    cursor: 
      user && 
      filteredWords.length > 0 && 
      !uiState.isMasterLoading && 
      !uiState.masterModeFailedWords[currentWord.word]
        ? 'pointer' 
        : 'not-allowed',
    borderRadius: '4px',
    fontSize: isMobile ? '0.9em' : '1em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: isMobile ? '100%' : '120px',
    maxWidth: isMobile ? '300px' : 'none',
    height: '36px',
  };

  const progressContainerStyle = {
    margin: isMobile ? '20px 10px' : '10px 0',
  };

  const footerStyle = {
    marginTop: isMobile ? '40px' : '60px',
    paddingTop: '20px',
    borderTop: `1px solid ${theme === 'light' ? '#e0e0e0' : '#444'}`,
    textAlign: 'center',
    color: currentTheme.secondaryText,
    fontSize: isMobile ? '0.8em' : '0.9em',
  };

  const footerLinksStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'center',
    gap: isMobile ? '10px' : '30px',
    marginBottom: '15px',
  };

  // 主页面渲染
  return (
  <>
    {/* ✅ 添加动画样式 */}
    <style>{`
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(-360deg); }
      }
      
      @keyframes spinDown {
        0% { 
          transform: rotate(0deg);
          animation-timing-function: ease-out;
        }
        100% { 
          transform: rotate(-360deg);
        }
      }
    `}</style>

    <div style={containerStyle}>
      {error && !error.includes('无') && !error.includes('发音') && (
        <p style={{ 
          color: currentTheme.errorText, 
          padding: '10px', 
          background: currentTheme.errorBg, 
          textAlign: 'center',
          borderRadius: '4px',
        }}>
          {error}
        </p>
      )}
      <div style={headerStyle}>
        <div style={titleSectionStyle}>
          <h1 style={{ 
            fontSize: isMobile ? '1.5em' : '2em', 
            margin: 0,
            wordBreak: 'break-word',
            color: currentTheme.text,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',  // 减小间距
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'center' : 'flex-start',
            lineHeight: '1.2',  // 添加行高控制
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>

              <img 
                src="/logo192.png" 
                alt="削词侠 LOGO" 
                style={{
                  height: '1em',
                  width: 'auto',
                  verticalAlign: 'middle',
                  position: 'relative',
                  top: '2px',
                  animation: isHovering 
                    ? 'spin 1s ease-in infinite' 
                    : 'spinDown 2s ease-out forwards',
                }}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              />
              
              削词侠 XIAOCIXIA
              <button
                onClick={toggleTheme}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1em',
                  cursor: 'pointer',
                  padding: '0',
                  margin: '0',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'transform 0.3s ease',  // 添加 ease
                  filter: theme === 'dark' ? 'brightness(1.2)' : 'none',
                  lineHeight: '1',
                  height: '1em',
                  transform: 'scale(0.8) translateY(2px)',
                  willChange: 'transform',  // 添加这个优化性能
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(0.96) translateY(2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(0.8) translateY(2px)';
                }}
                title={theme === 'light' ? '切换到夜间模式' : '切换到日间模式'}
                aria-label={theme === 'light' ? '切换到夜间模式' : '切换到日间模式'}
              >
                {theme === 'light' ? '☀️' : '🌙'}
              </button>
            </span>
          </h1>
        </div>
        <div style={centerSectionStyle}>
          <p style={{ 
            fontSize: isMobile ? '1em' : '1.2em', 
            margin: '0 0 10px 0', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '5px'
          }}>
            当前级别:
            <select
              value={category}
              onChange={handleCategoryChange}
              onMouseDown={handleSelectEvent}
              onMouseUp={handleSelectEvent}
              onTouchStart={handleSelectEvent}
              onFocus={handleSelectEvent}
              onBlur={handleSelectEvent}
              disabled={words.length === 0 && error}
              style={{
                marginLeft: isMobile ? '0' : '10px',
                marginTop: isMobile ? '5px' : '0',
                padding: '5px',
                fontSize: isMobile ? '0.9em' : '1em',
                border: 'none',
                borderBottom: `2px solid ${currentTheme.border}`,
                borderRadius: '0',
                background: currentTheme.background,
                color: currentTheme.text,
                outline: 'none',
                width: 'auto',
                maxWidth: isMobile ? '250px' : 'none',
                textAlign: 'center',
                opacity: words.length === 0 && error ? 0.5 : 1,
              }}
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                  {user ? ` 已完成 ${completedCounts[cat.value] || 0} 次学习` : ''}
                </option>
              ))}
            </select>
          </p>
          
          {/* 搜索框 */}
          <div style={{ marginTop: '15px', width: '100%', padding: isMobile ? '0 10px' : '0' }}>
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '10px',
              width: '100%'
            }}>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="搜单词..."
                style={{
                  padding: '8px 12px',
                  fontSize: isMobile ? '0.9em' : '1em',
                  border: '2px solid #ddd',
                  borderRadius: '8px',
                  outline: 'none',
                  width: isMobile ? '100%' : '300px',
                  maxWidth: '300px',
                  transition: 'border-color 0.3s',
                }}
                onFocus={(e) => e.target.style.borderColor = '#1890ff'}
                onBlur={(e) => e.target.style.borderColor = '#ddd'}
              />
              {uiState.searchMode && (
                <button
                  onClick={exitSearchMode}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#333'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
                  style={{
                    padding: '8px 12px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: isMobile ? '1.2em' : '1.3em',
                    width: isMobile ? '100%' : 'auto',
                    maxWidth: isMobile ? '300px' : 'none',
                    fontWeight: 'bold',
                    color: '#666',
                    transition: 'color 0.2s ease'
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            {uiState.searchMode && (
              <p style={{ fontSize: '0.9em', color: '#666', margin: '5px 0' }}>
                找到 {searchResults.length} 个结果
              </p>
            )}
          </div>
        </div>

        <div style={userSectionStyle}>
          {user ? (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: isMobile ? 'center' : 'flex-end', 
              height: '100%',
              gap: '10px'
            }}>
              <div style={{ flex: 1 }}></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                <img
                  src={userProfile.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile.displayName || user.email.charAt(0))}&size=40&background=1890ff&color=fff`}
                  alt="头像"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                />
                <span style={{ fontSize: '1em', color: theme === 'dark' ? '#e0e0e0' : '#333' }}>
                  {userProfile.displayName || '未设置昵称'}
                </span>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: isMobile ? 'center' : 'flex-end', 
                width: '100%',
                gap: isMobile ? '15px' : '0'
              }}>
                <button
                  onClick={() => setCurrentPage('personal')}
                  style={{
                    padding: '5px 10px',
                    border: 'none',
                    background: 'none',
                    color: '#1890ff',
                    cursor: 'pointer',
                    fontSize: isMobile ? '0.9em' : '1em',
                    marginRight: isMobile ? '0' : '35px',
                  }}
                >
                  个人中心
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    padding: '5px 10px',
                    border: 'none',
                    background: 'none',
                    color: theme === 'dark' ? '#cccccc' : '#666',
                    cursor: 'pointer',
                    fontSize: isMobile ? '0.9em' : '1em',
                  }}
                >
                  登出
                </button>
              </div>
            </div>
            
          ) : (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: isMobile ? 'center' : 'flex-end',
              width: '100%',
              maxWidth: isMobile ? '300px' : 'none',
              margin: isMobile ? '0 auto' : '0',
              position: 'relative',
            }}>
              {/* 🇨🇳 中国站引导横幅 */}
              <div style={{
                backgroundColor: '#E6F7FF',
                border: '1px solid #91D5FF',
                borderRadius: '8px',
                padding: '12px 16px',
                marginBottom: '15px',
                width: '100%',
                textAlign: 'center',
                boxSizing: 'border-box',
              }}>
                <div style={{ 
                  color: '#0050B3',
                  fontSize: '0.9em',
                  fontWeight: '600',
                  marginBottom: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}>
                  🇨🇳 中国大陆用户请使用中国站
                </div>
                <div style={{ 
                  color: '#0050B3', 
                  fontSize: '0.75em',
                  marginBottom: '8px'
                }}>
                  更快的速度 · 更稳定的服务
                </div>
                <a 
                  href="https://xiaocixia.cn"
                  style={{
                    display: 'inline-block',
                    backgroundColor: '#1890FF',
                    color: 'white',
                    padding: '6px 20px',
                    borderRadius: '4px',
                    textDecoration: 'none',
                    fontSize: '0.85em',
                    fontWeight: '500',
                    transition: 'background-color 0.3s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#40a9ff'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#1890FF'}
                >
                  前往中国站 →
                </a>
              </div>

              {authError && (
                <p style={{ 
                  color: '#666', 
                  margin: '5px 0', 
                  textAlign: isMobile ? 'center' : 'right',
                  fontSize: isMobile ? '0.85em' : '0.9em'
                }}>
                  {authError}
                </p>
              )}
              <form onSubmit={handleLogin} style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: isMobile ? 'center' : 'flex-end',
                width: '100%'
              }}>
                <input
                  ref={emailInputRef}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={handleLoginFocus}
                  placeholder="电子邮件"
                  style={{
                    marginBottom: '5px',
                    padding: '5px',
                    width: '100%',
                    border: 'none',
                    borderBottom: '2px solid #000',
                    borderRadius: '0',
                    outline: 'none',
                    fontSize: '1em',
                  }}
                />
                <input
                  ref={passwordInputRef}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={handleLoginFocus}
                  placeholder="密码"
                  style={{
                    marginBottom: '5px',
                    padding: '5px',
                    width: '100%',
                    border: 'none',
                    borderBottom: '2px solid #000',
                    borderRadius: '0',
                    outline: 'none',
                    fontSize: '1em',
                  }}
                />
                <div style={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between', 
                  width: isMobile ? '100%' : '200px',
                  gap: isMobile ? '8px' : '0'
                }}>
                  <button type="submit" style={{ 
                    padding: '5px 10px',
                    fontSize: isMobile ? '0.9em' : '1em',
                    width: isMobile ? '100%' : 'auto'
                  }}>
                    登录
                  </button>
                  <button type="button" onClick={handleRegister} style={{ 
                    padding: '5px 10px',
                    fontSize: isMobile ? '0.9em' : '1em',
                    width: isMobile ? '100%' : 'auto'
                  }}>
                    注册
                  </button>
                  <button type="button" onClick={handleResetPassword} style={{ 
                    padding: '5px 10px',
                    fontSize: isMobile ? '0.9em' : '1em',
                    width: isMobile ? '100%' : 'auto'
                  }}>
                    找回密码
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
      {words.length === 0 && !error && <p style={{ textAlign: 'center' }}>加载中...</p>}
      {words.length === 0 && error && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p style={{ color: 'red', fontSize: '1.2em' }}>
            {error}
            <br />
            请检查 public 文件夹中的 words.json 是否存在，或尝试刷新页面。
          </p>
        </div>
      )}
      {words.length > 0 && (
        <div style={{ textAlign: 'center' }}>
          {/* 搜索模式 */}
          {uiState.searchMode ? (
            searchResults.length === 0 ? (
              <p style={{ color: '#666', padding: '20px', fontSize: '1.2em' }}>
                未找到包含 &quot;{searchQuery}&quot; 的单词
              </p>
            ) : (
              <>
                <div style={{ margin: '10px 0', color: '#666' }}>
                  <p>搜索模式 - 第 {currentWordIndex + 1} 个，共 {searchResults.length} 个结果</p>
                </div>
                <h3
                  style={wordDisplayStyle}
                  className={uiState.isIncorrectWord ? 'word-incorrect' : ''}
                >
                  {currentWord.word || '无单词'}
                </h3>
                
                {/* 发音切换按钮 - 只保留一组 */}
                <div style={{ marginBottom: '10px' }}>
                  <button
                    onClick={() => {
                      // 先停止当前播放
                      if (audioRef.current) {
                        audioRef.current.pause();
                        audioRef.current.src = '';
                        audioRef.current = null;
                      }
                      
                      setPronunciationType('us');
                      
                      // 播放搜索结果的发音
                      const wordToPlay = searchResults[currentWordIndex];
                      if (wordToPlay?.audio?.us && wordToPlay.audio.us !== '') {
                        setTimeout(() => playAudio(wordToPlay.audio.us, wordToPlay.word, false), 100);
                      }
                    }}
                    style={{
                      padding: '5px 15px',
                      background: pronunciationType === 'us' ? '#1890ff' : '#f0f0f0',
                      color: pronunciationType === 'us' ? 'white' : '#666',
                      border: `1px solid ${theme === 'dark' ? '#555' : '#ddd'}`,
                      borderRadius: '4px 0 0 4px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                    }}
                  >
                    美式
                  </button>
                  <button
                    onClick={() => {
                      // 先停止当前播放
                      if (audioRef.current) {
                        audioRef.current.pause();
                        audioRef.current.src = '';
                        audioRef.current = null;
                      }
                      
                      setPronunciationType('uk');
                      
                      // 播放搜索结果的发音
                      const wordToPlay = searchResults[currentWordIndex];
                      if (wordToPlay?.audio?.uk && wordToPlay.audio.uk !== '') {
                        setTimeout(() => playAudio(wordToPlay.audio.uk, wordToPlay.word, false), 100);
                      }
                    }}
                    style={{
                      padding: '5px 15px',
                      background: pronunciationType === 'uk' ? '#1890ff' : '#f0f0f0',
                      color: pronunciationType === 'uk' ? 'white' : '#666',
                      border: `1px solid ${theme === 'dark' ? '#555' : '#ddd'}`,
                      borderRadius: '0 4px 4px 0',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                    }}
                  >
                    英式
                  </button>
                </div>

                <p style={{ fontSize: '1.2em', display: 'inline-flex', alignItems: 'center' }}>
                  音标: {currentWord.phonetic?.[pronunciationType] || '无'}
                  {currentWord.audio?.[pronunciationType] && currentWord.audio[pronunciationType] !== '' ? (
                    <>
                      <button
                        aria-label="播放发音"
                        onClick={() => playAudio(currentWord.audio[pronunciationType], currentWord.word, false)}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'translateY(2px)'}
                        onMouseUp={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        style={{
                          marginLeft: '20px',
                          position: 'relative',
                          top: '3px',
                          fontSize: '0.8em',
                          cursor: 'pointer',
                          padding: '2px',
                          lineHeight: '1',
                          background: 'none',
                          border: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          transition: 'transform 0.1s ease',
                        }}
                        title="单次播放"
                      >
                        🔊
                      </button>
                      {/* <button
                        aria-label={loopPlay ? '停止循环播放' : '循环播放'}
                        onClick={toggleLoopPlay}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'translateY(2px)'}
                        onMouseUp={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        style={{
                          marginLeft: '5px',
                          position: 'relative',
                          top: '3px',
                          fontSize: '0.8em',
                          cursor: 'pointer',
                          padding: '2px',
                          lineHeight: '1',
                          color: loopPlay ? 'blue' : 'black',
                          background: 'none',
                          border: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          transition: 'transform 0.1s ease',
                        }}
                        title={loopPlay ? '停止循环' : '循环播放'}
                      >
                        🔁
                      </button> */}
                      <button
                        aria-label={loopPlay ? '停止循环播放' : '循环播放'}
                        onClick={toggleLoopPlay}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'translateY(2px)'}
                        onMouseUp={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        style={{
                          marginLeft: '5px',
                          position: 'relative',
                          top: '3px',
                          fontSize: '0.8em',
                          cursor: 'pointer',
                          padding: '2px',
                          lineHeight: '1',
                          color: loopPlay ? 'blue' : 'black',
                          background: 'none',
                          border: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          transition: 'transform 0.1s ease',
                        }}
                        title={loopPlay ? '停止循环' : '循环播放'}
                      >
                        {loopPlay ? '⏸️' : '🔁'}  {/* ✅ 修改：循环播放时显示暂停图标 */}
                      </button>
                    </>
                  ) : (
                    <span style={{ fontSize: '0.8em', color: 'red', marginLeft: '20px' }}>无发音</span>
                  )}
                </p>
                <p style={{ fontSize: '1.2em' }}>中文释义: {currentWord.meaning_cn || '无'}</p>
                <p style={{ fontSize: '1em', color: '#666' }}>
                  类别: {Array.isArray(currentWord.category) ? currentWord.category.join(', ') : currentWord.category || '未分类'}
                </p>
                
                <div style={{ margin: '20px 0', display: 'flex', justifyContent: 'center', gap: '10px' }}>
                  <button
                    onClick={() => {
                      // 停止当前播放
                      if (audioRef.current) {
                        audioRef.current.pause();
                        audioRef.current.src = '';
                        audioRef.current = null;
                      }
                      setCurrentWordIndex(Math.max(0, currentWordIndex - 1));
                    }}
                    disabled={currentWordIndex === 0}
                    style={{
                      padding: '8px 16px',
                      background: currentWordIndex === 0 ? '#f0f0f0' : '#1890ff',
                      color: currentWordIndex === 0 ? '#999' : 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: currentWordIndex === 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    上一个
                  </button>
                  <button
                    onClick={() => {
                      // 停止当前播放
                      if (audioRef.current) {
                        audioRef.current.pause();
                        audioRef.current.src = '';
                        audioRef.current = null;
                      }
                      setCurrentWordIndex(Math.min(searchResults.length - 1, currentWordIndex + 1));
                    }}
                    disabled={currentWordIndex === searchResults.length - 1}
                    style={{
                      padding: '8px 16px',
                      background: currentWordIndex === searchResults.length - 1 ? '#f0f0f0' : '#1890ff',
                      color: currentWordIndex === searchResults.length - 1 ? '#999' : 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: currentWordIndex === searchResults.length - 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    下一个
                  </button>
                </div>
              </>
            )
          ) : (
            /* 学习模式 */
            filteredWords.length === 0 && Object.keys(dailyProgress).length > 0 ? (
              <div style={{ textAlign: 'center' }}>
                <p style={{ color: 'green', padding: '10px', fontSize: '1.5em' }}>恭喜你！已完成本次学习。加油哦！</p>
                <p style={{ color: '#666', fontSize: '1.1em', marginTop: '10px' }}>按任意键继续学习</p>
                <p style={{ color: '#999', fontSize: '0.9em', marginTop: '5px' }}></p>
              </div>
            ) : filteredWords.length === 0 ? (
              <p style={{ color: 'red', padding: '10px' }}>无 {category} 级别的未掌握单词</p>
            ) : (
              <>
                <h3
                  style={wordDisplayStyle}
                  className={uiState.isIncorrectWord ? 'word-incorrect' : ''}
                >
                  {uiState.isInMasterMode && uiState.masterModeWord === currentWord.word ? (
                    // 削词模式：根据输入逐个显示字母
                    <>
                      {currentWord.word.split('').map((letter, index) => {
                        const inputLower = inputValue.toLowerCase();
                        const isRevealed = index < inputLower.length;
                        return (
                          <span
                            key={index}
                            style={{
                              visibility: isRevealed ? 'visible' : 'hidden',
                              color: isRevealed ? currentTheme.text : 'transparent',  // 修改这里：从 '#000' 改为 currentTheme.text
                            }}
                          >
                            {letter}
                          </span>
                        );
                      })}
                    </>
                  ) : (
                    // 正常模式：显示完整单词
                    currentWord.word || '无单词'
                  )}
                </h3>
                
                {/* 发音切换按钮 */}
                <div style={{ marginBottom: '10px' }}>
                  <button
                    onClick={() => {// 停止当前播放
                      if (audioRef.current) {
                        audioRef.current.pause();
                        audioRef.current.src = '';
                        audioRef.current = null;
                      }
                      setPronunciationType('us')}}
                    style={{
                      padding: '5px 15px',
                      background: pronunciationType === 'us' ? '#1890ff' : '#f0f0f0',
                      color: pronunciationType === 'us' ? 'white' : '#666',
                      border: `1px solid ${theme === 'dark' ? '#555' : '#ddd'}`,
                      borderRadius: '4px 0 0 4px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                    }}
                  >
                    美式
                  </button>
                  <button
                    onClick={() => {// 停止当前播放
                      if (audioRef.current) {
                        audioRef.current.pause();
                        audioRef.current.src = '';
                        audioRef.current = null;
                      }
                    setPronunciationType('uk')}}
                    style={{
                      padding: '5px 15px',
                      background: pronunciationType === 'uk' ? '#1890ff' : '#f0f0f0',
                      color: pronunciationType === 'uk' ? 'white' : '#666',
                      border: `1px solid ${theme === 'dark' ? '#555' : '#ddd'}`,
                      borderRadius: '0 4px 4px 0',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                    }}
                  >
                    英式
                  </button>
                </div>

                <p style={{ fontSize: '1.2em', display: 'inline-flex', alignItems: 'center' }}>
                  音标: {currentWord.phonetic?.[pronunciationType] || '无'}
                  {currentWord.audio?.[pronunciationType] && currentWord.audio[pronunciationType] !== '' ? (
                    <>
                      <button
                        aria-label="播放发音"
                        onClick={() => playAudio(currentWord.audio[pronunciationType], currentWord.word, false)}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'translateY(2px)'}
                        onMouseUp={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        style={{
                          marginLeft: '20px',
                          position: 'relative',
                          top: '3px',
                          fontSize: '0.8em',
                          cursor: 'pointer',
                          padding: '2px',
                          lineHeight: '1',
                          background: 'none',
                          border: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          transition: 'transform 0.1s ease',
                        }}
                        title="单次播放"
                      >
                        🔊
                      </button>
                      {/* <button
                        aria-label={loopPlay ? '停止循环播放' : '循环播放'}
                        onClick={toggleLoopPlay}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'translateY(2px)'}
                        onMouseUp={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        style={{
                          marginLeft: '5px',
                          position: 'relative',
                          top: '3px',
                          fontSize: '0.8em',
                          cursor: 'pointer',
                          padding: '2px',
                          lineHeight: '1',
                          color: loopPlay ? 'blue' : 'black',
                          background: 'none',
                          border: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          transition: 'transform 0.1s ease',
                        }}
                        title={loopPlay ? '停止循环' : '循环播放'}
                      >
                        🔁
                      </button> */}
                      <button
                        aria-label={loopPlay ? '停止循环播放' : '循环播放'}
                        onClick={toggleLoopPlay}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'translateY(2px)'}
                        onMouseUp={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                        style={{
                          marginLeft: '5px',
                          position: 'relative',
                          top: '3px',
                          fontSize: '0.8em',
                          cursor: 'pointer',
                          padding: '2px',
                          lineHeight: '1',
                          color: loopPlay ? 'blue' : 'black',
                          background: 'none',
                          border: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          transition: 'transform 0.1s ease',
                        }}
                        title={loopPlay ? '停止循环' : '循环播放'}
                      >
                        {loopPlay ? '⏸️' : '🔁'}  {/* ✅ 修改：循环播放时显示暂停图标 */}
                      </button>
                    </>
                  ) : (
                    <span style={{ fontSize: '0.8em', color: 'red', marginLeft: '20px' }}>无发音</span>
                  )}
                </p>
                
                
                <p style={{ fontSize: '1.2em' }}>中文释义: {currentWord.meaning_cn || '无'}</p>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '0px',  // 输入框和圆圈的间距
                    width: '100%',
                    maxWidth: isMobile ? '300px' : 'none',
                  }}>

                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      onFocus={() => {
                        updateUiState({ isWordInputFocused: true, isInputIncorrect: false });
                      }}
                      placeholder="输入单词"
                      className={uiState.isInputIncorrect ? 'input-incorrect' : ''}
                      style={inputFieldStyle}
                    />
                    {uiState.showLoading && (
                      <div
                        style={{
                          // width: '20px',
                          // height: '20px',
                          // border: '2px solid #f3f3f3',
                          // borderTop: '2px solid #3498db', // 这是从顶部开始旋转
                          // borderRadius: '50%',
                          // animation: 'spin 1s linear infinite',
                          // animationDirection: 'reverse',   // ← 反向旋转！
                          // flexShrink: 0,  // 防止圆圈被压缩
                          // position: 'relative',   // 相对定位
                          // left: '-12px',          // 向左移动10px
                          // top: '13px',            // 向下移动15px
                          width: '20px',
                          height: '20px',
                          border: '2px solid #ffffff',
                          borderBottom: '2px solid #3498db',  // ✅ 改为 borderBottom，从底部开始
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          animationDirection: 'reverse',   // ← 反向旋转！
                          flexShrink: 0,
                          position: 'relative',
                          left: '-12px',
                          top: '12px',
                        }}
                      />
                    )}
                    
                  </div>
                  <div style={buttonContainerStyle}>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.9em' }}>
                      <input
                        type="checkbox"
                        checked={autoPlay}
                        onChange={toggleAutoPlay}
                        style={{ marginRight: '5px' }}
                        disabled={words.length === 0 && error}
                      />
                      自动播放发音
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', fontSize: '0.9em' }}>
                      <input
                        type="checkbox"
                        checked={keyboardSound}
                        onChange={toggleKeyboardSound}
                        style={{ marginRight: '5px' }}
                        disabled={words.length === 0 && error}
                      />
                      键盘声音
                    </label>
                  </div>

                  <div style={{ marginTop: '10px', fontSize: '0.9em', textAlign: 'center' }}>
                    {/* 只在未登录时显示提示文字 */}
                    {!user && (
                      <p style={{ margin: '5px 0', color: '#d9d9d9' }}>
                        如认识该词，登录可削掉，需时再释放。
                      </p>
                    )}
                    
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                      <button
                        onClick={() => handleManualMaster(currentWord.word)}
                        disabled={
                          !user || 
                          filteredWords.length === 0 || 
                          uiState.isMasterLoading || 
                          uiState.masterModeFailedWords[currentWord.word]
                        }
                        style={masterButtonStyle}
                        title={
                          uiState.masterModeFailedWords[currentWord.word]
                            ? '削词失败，请继续学习'
                            : uiState.isInMasterMode
                            ? '连续削词模式中，输入错误将退出'
                            : '点击进入削词模式'
                        }
                      >
                        {uiState.isMasterLoading ? (
                          <>
                            削词中
                            <div className="loading-spinner"></div>
                          </>
                        ) : uiState.isInMasterMode ? (
                          '连续削词中...'
                        ) : (
                          '进入削词模式（Ctrl + x）'
                        )}
                      </button>
                      
                      {/* 退出削词模式按钮 - 可选 */}
                      {uiState.isInMasterMode && (
                        <button
                          onClick={() => {
                            updateUiState({
                              isInMasterMode: false,
                              masterModeWord: ''
                            });
                            setInputValue('');
                            if (inputRef.current) {
                              inputRef.current.focus();
                            }
                            console.log('手动退出削词模式');
                          }}
                          style={{
                            padding: isMobile ? '10px 15px' : '8px 20px',
                            background: '#ff4d4f',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            fontSize: isMobile ? '0.9em' : '1em',
                            width: isMobile ? '100%' : 'auto',
                            maxWidth: isMobile ? '300px' : 'none'
                          }}
                        >
                          退出削词模式（Ctrl + c）
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* 递减 */}
                <div style={progressContainerStyle}>
                  <p style={{ fontSize: isMobile ? '0.9em' : '1em' }}>
                    学习进度 ({progressStats.studiedThisCycle}/{progressStats.currentCycleTotalWords}): {progressStats.currentCycleProgress}%
                    <span style={{ fontSize: '0.8em', color: currentTheme.secondaryText, marginLeft: '10px' }}>
                      {/* (本级别词量: {progressStats.totalWords}) */}
        
                    </span>
                  </p>
                  {/* 反转进度条：使用剩余数量而不是已完成数量 */}
                  <progress 
                    value={progressStats.remainingWords} 
                    max={progressStats.currentCycleTotalWords || 1} 
                    style={{ width: '100%' }} 
                  />
                  <p style={{ fontSize: isMobile ? '0.9em' : '1em' }}>
                    削词进度 ({progressStats.totalMasteredForProgress}/{progressStats.totalWords}): {progressStats.masteredProgress}%
                    <span style={{ fontSize: '0.8em', color: currentTheme.secondaryText, marginLeft: '10px' }}>
                      {/* (已削词: {progressStats.manualMasteredCount}) */}
                    </span>
                  </p>
                  <progress 
                    value={progressStats.totalMasteredForProgress} 
                    max={progressStats.totalWords || 1} 
                    style={{ width: '100%' }} 
                  />
                </div>
              </>
            )
          )}
        </div>
      )}

      {/* 页脚 */}
      <footer style={footerStyle}>
        <div style={footerLinksStyle}>
          <button
            onClick={() => setCurrentPage('about')}
            style={{
              background: 'none',
              border: 'none',
              color: '#1890ff',
              cursor: 'pointer',
              fontSize: '1em',
              textDecoration: 'none',
              padding: '5px',
            }}
          >
            关于我们
          </button>
          <button
            onClick={() => setCurrentPage('contact')}
            style={{
              background: 'none',
              border: 'none',
              color: '#1890ff',
              cursor: 'pointer',
              fontSize: '1em',
              textDecoration: 'none',
              padding: '5px',
            }}
          >
            联系我们
          </button>
          <button
            onClick={() => setCurrentPage('privacy')}
            style={{
              background: 'none',
              border: 'none',
              color: '#1890ff',
              cursor: 'pointer',
              fontSize: '1em',
              textDecoration: 'none',
              padding: '5px',
            }}
          >
            隐私政策
          </button>
        </div>
        <p style={{ margin: '10px 0', color: '#999' }}>
          © 2025 削词侠 XIAOCIXIA. All rights reserved.
        </p>
      </footer>
    </div>
    </>
  );
}

export default App;